import { getAuthUser } from '@/utils/auth'
import { useJoinTeamMutation } from '@features/connections/useUserTeams'
import {
  useFollowOrgMutation,
  useIsOrgSubscriber,
} from '@features/organizations/hooks/useUserOrgs'
import { orgKeys } from '@features/organizations/model/query-keys'
import { OrgDetailScreen } from '@features/organizations/screens/DetailScreen'
import { GiveMoreScreen } from '@features/organizations/screens/GiveMoreScreen'
import { Props } from '@features/organizations/screens/props'
import { useRoadblockAction } from '@features/roadblock'
import { Layout } from '@layouts/Layout'
import { isNullish, isNumericId } from '@purposity/utils'
import { analytics } from '@services/Analytics'
import { dehydrate, QueryClient } from '@tanstack/react-query'
import { sdkAsAdmin } from 'lib/as-admin'
import type { GetServerSidePropsContext } from 'next'
import { useRouter } from 'next/router'
import { useEffect } from 'react'
import type { NextPageWithLayout } from 'types/next'
import * as z from 'zod'

const SEC_CONSIDERED_STALE = 1
const PRERENDER_COUNT = 1

const Params = z.object({
  alias: z.string(),
})
type Params = z.infer<typeof Params>

export const getServerSideProps = async (
  context: GetServerSidePropsContext<Params>
) => {
  const user = await getAuthUser(context)
  const params = Params.partial().parse(context.params)
  const split_alias = params.alias?.split('?')
  const alias = split_alias?.[0]

  if (isNullish(alias)) {
    return {
      notFound: true,
    }
  }

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 1000 * SEC_CONSIDERED_STALE,
        cacheTime: Infinity,
        meta: {
          headers: sdkAsAdmin.__headers,
        },
      },
    },
  })

  if (isNumericId(+alias)) {
    const id = +alias
    const data = await queryClient.fetchQuery(orgKeys.detailById(id))
    const org = data.organization

    if (org == null) {
      return {
        notFound: true,
      }
    } else {
      return {
        redirect: {
          destination: `/organization/${org.alias}`,
          permanent: true,
        },
      }
    }
  } else {
    const data = await queryClient.fetchQuery(orgKeys.detail(alias))
    const org = data.organization?.[0]

    if (org == null) {
      return {
        notFound: true,
      }
    } else if (org.alias !== alias) {
      return {
        redirect: {
          destination: '/organization/' + org.alias,
          permanent: true,
        },
      }
    } else {
      return {
        props: {
          user,
          org,
          dehydratedState: dehydrate(queryClient),
        },
      }
    }
  }
}

function Wrapper(props: Props) {
  const router = useRouter()
  const { team } = router.query
  const mutation = useJoinTeamMutation()
  const followMutation = useFollowOrgMutation()

  const isFollower = useIsOrgSubscriber({
    v_alias: props.org?.alias || '',
  })

  const { callback } = useRoadblockAction({
    trigger: () => {
      mutation.mutate({ id: 0, alias: team as string, v_alias: team as string })
      followMutation.mutate(props.org)

      analytics.track('Organization Follow', {
        id: props.org?.id,
        orgName: props.org?.alias,
        team: team as string,
      })

      router.replace(`/organization/${props.org?.alias}`)
    },
    referral: {
      entity: 'view',
      method: 'view',
      params: {
        pathname: `/organization/${props.org?.alias || ''}?team=${team as string}`,
      },
    },
  })

  useEffect(() => {
    if (team && typeof team === 'string' && !isFollower) {
      callback()
    }
  }, [team])

  if (props.org == null) {
    return null
  } else {
    if (props.org.alias === 'givemore') {
      return <GiveMoreScreen />
    } else {
      return <OrgDetailScreen />
    }
  }
}

const OrganizationDetailPage: NextPageWithLayout<Props, Props> = Wrapper

OrganizationDetailPage.getLayout = function getLayout(
  page: React.ReactElement
) {
  return <Layout user={page.props.user}>{page}</Layout>
}

export default OrganizationDetailPage
